<template>
  <!--
    Add classes to <li> change icons
    Вывод средств - .withdrawal
    Перевод средств - .transfer
  -->
  <li
    class="list-item"
    :class="{
      'loading': TRANSACTIONS.blink,
      'selected': isTransactionSelected,
      'unselected': isUnfocused,
      [getIconStyle(transaction)]: true,
      [getMultiPayClass]: true,
      [getBillTypeClass]: true,
    }"
    @click="SELECT_TRANSACTION(transaction)"
  >
    <div class="list_item-content">
      <div class="list_item-content-box">
        <div ref="name" class="list_item-name">

          <template v-if="transactionTypeIs(transaction, 'orders')">
            <span
              v-if="transaction.senderProperties.name"
              v-highlight="highlightSettings"
            >
              {{ transaction.senderProperties.name }}
            </span>
          </template>

          <span v-if="transactionTypeIs(transaction, 'partner')">
            {{ getPartnerText }}
          </span>

          <span v-if="transactionTypeIs(transaction, 'bills')">
            {{ getBillText }}
          </span>

          <template v-if="!transaction.senderProperties.name">
            <span
              v-if="transaction.account_number"
              v-highlight="highlightSettings"
            >
              <template v-if="isApplePaymentMethod && isRefundEventName">
                {{ getFormattedAppleMethodName }}
              </template>
              <template v-else-if="isApplePaymentMethod && !isRefundEventName">
                {{ transaction.card_ps_type }}
                {{ transaction.account_number | accountNumberShort }}
              </template>
              <template v-else-if="isGooglePaymentMethod">
                {{ getFormattedGoogleAccountNumber }}
              </template>
              <template v-else-if="isCurrencyCrypto && !transactionTypeIs(transaction, 'bills')">
                {{ $t('common.wallet') }}
              </template>
              <template v-else-if="isDeviceSize.mobileBig">
                {{ transaction.account_number | accountNumberShortMobile }}
              </template>
              <template v-else>
                {{ transaction.account_number | accountNumber }}
              </template>
            </span>
            <span
              v-else-if="isCurrencyCrypto && !transactionTypeIs(transaction, 'bills')"
              v-text="$t('common.wallet')"
            ></span>
            <span
              v-else-if="isQiwiPaymentMethod"
              v-highlight="highlightSettings"
            >
              Qiwi
            </span>
            <span
              v-else-if="isYoomoneyPaymentMethod"
              v-highlight="highlightSettings"
            >
              YooMoney
            </span>
            <span
              v-else-if="isApplePaymentMethod"
              v-highlight="highlightSettings"
            >
              Apple Pay
            </span>
            <span
              v-else-if="isGooglePaymentMethod"
              v-highlight="highlightSettings"
            >
              Google Pay
            </span>
            <span
              v-else-if="isPayMePaymentMethod"
              v-highlight="highlightSettings"
            >
              PayMe
            </span>
            <span
              v-else-if="isYandexPaymentMethod"
              v-highlight="highlightSettings"
            >
              Yandex Pay
            </span>
          </template>
        </div>

        <div ref="group" class="list_item-group">
          <div class="list_item-id">
            <template v-if="!isDeviceSize.mobileBig">
              <span
                v-if="transactionTypeIs(transaction, 'bills')"
                v-highlight="highlightSettings"
                class="font-weight--bold"
              >
                <template v-if="['SUBSCRIPTION', 'TARIFF', 'TICKET', 'TIPS'].includes(transaction.type)">
                  {{ getTextByOrderType(transaction.type) }}
                </template>
                <template v-else>
                  {{ getTextByOrderType(transaction.multiBill ? 'MULTIBILL' : 'BILL') }}
                </template>
              </span>
              <span
                v-else
                v-highlight="highlightSettings"
                class="font-weight--bold"
              >
                {{ getTextByOrderType(transaction.type || 'BILL') }}
              </span>
            </template>
            <span v-highlight="highlightSettings" class="font-weight--bold">
              <template v-if="transaction.type === 'PURCHASE_BILL'">
                {{ transaction.multiBill ? transaction.public_id : transaction.partner_payment_id || transaction.public_id }}
              </template>
              <template v-else>
                {{ transaction.partner_payment_id || transaction.public_id }}
              </template>
            </span>
          </div>
          <div v-if="transaction.description" class="list_item-description">
            <span
              v-if="transactionTypeIs(transaction, 'partner')"
              class="font-weight--400"
              v-text="$t(transaction.description)"
            ></span>
            <span
              v-else
              class="font-weight--400"
              v-text="transaction.description"
            ></span>
          </div>
        </div>
      </div>

      <div class="list_item-content-box" :class="{ 'processing-safe-deal': isProcessingSafeDeal }">
        <div ref="price" class="list_item-price">
          <span v-if="isProcessingSafeDeal" :class="$style.safeDealIcon">
            <lock-outline-icon />
          </span>
          <span
            v-if="transactionTypeIs(transaction, 'bills')"
            v-highlight="highlightSettings"
            class="font-weight--semi-bold"
            :class="[getTypeClass(transaction.type)]"
          >
            {{ formattedBillAmount }}
          </span>
          <span
            v-else
            v-highlight="highlightSettings"
            class="font-weight--semi-bold"
            :class="[getTypeClass(transaction.type)]"
          >
            {{ formattedAmount }}
          </span>
        </div>
        <div ref="time" class="list_item-time">
          <span class="font-weight--400">
            {{ transaction.created_at | date('HH:mm') }}
          </span>
        </div>
      </div>

      <div class="list_item-content-box">
        <div
          v-if="transaction.senderProperties.comment && transactionTypeIs(transaction, 'orders')"
          class="list_item-comment"
        >
          {{ transaction.senderProperties.comment }}
        </div>
        <div v-if="isShownRating" class="list_item-rating">
          <transaction-rating :rating="transaction.rating" />
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import highlight from 'vue-highlight-text/public/directive.min';

import { formatAmount } from 'lib/utils/amount';

import LockOutlineIcon from 'atoms/icons/basic/LockOutline';
import TransactionRating from 'molecules/transaction/details/TransactionRating';

import formatAccountNumber from './format-account-number';

import { CASUAL, SUBSCRIPTION, TARIFF } from 'modules/Cards/const';

import {
  getIconStyle,
  getTypeClass,
  getCurrencyClass,
  transactionTypeIs
} from '@/services/mixins';

export default {
  name: 'TransactionItem',
  mixins: [
    getIconStyle,
    getTypeClass,
    getCurrencyClass,
    transactionTypeIs,
  ],
  components: {
    LockOutlineIcon,
    TransactionRating,
  },
  directives: {
    highlight,
  },
  props: {
    transaction: {
      type: Object,
    },
  },
  mounted() {
    const name = this.$refs.name;
    const price = this.$refs.price;
    const time = this.$refs.time;
    const group = this.$refs.group;

    name.style.width = `calc(100% - ${price.offsetWidth}px)`;
    group.style.width = `calc(100% - ${time.offsetWidth}px)`;
  },
  data() {
    return {
      show: false,
    };
  },
  computed: {
    currency() {
      return this.transaction.currency;
    },
    isApplePaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'APPLE';
    },
    isGooglePaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'GOOGLE';
    },
    isQiwiPaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'QIWI';
    },
    isYoomoneyPaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'YOOMONEY';
    },
    isPayMePaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'PAYME';
    },
    isYandexPaymentMethod() {
      return this.transaction.payment_method && this.transaction.payment_method === 'YANDEX';
    },
    isRefundEventName() {
      return this.transaction.event_name === 'REFUND';
    },
    getFormattedAppleMethodName() {
      if (!this.isApplePaymentMethod) {
        return '';
      }
      let method = this.transaction.payment_method;
      let methodName = method[0].toUpperCase() + method.toLowerCase().slice(1);

      return `${methodName} Pay`;
    },
    getFormattedGoogleAccountNumber() {
      if (!this.isGooglePaymentMethod) {
        return '';
      }

      const { account_number: accountNumber } = this.transaction;
      const { card_ps_type: cardPsType } = this.transaction;

      if (this.$validate.isNull(accountNumber)) {
        return '';
      }

      return formatAccountNumber(accountNumber, cardPsType);
    },
    highlightSettings() {
      return {
        sensitive: false,
        keyword: this.TRANSACTIONS.searchText,
        overWriteStyle: {
          display: 'inline-block',
          background: '#219653',
          color: 'inherit',
        },
      };
    },
    getBillText() {
      return this.transaction.billPaymentEnabled ? this.$t('common.opened') : this.$t('is.stopped');
    },
    getPartnerText() {
      return this.transaction.partnerProjectName;
    },
    isTransactionSelected() {
      return this.transaction.public_id && this.SELECTED_TRANSACTION.public_id && this.transaction.public_id === this.SELECTED_TRANSACTION.public_id;
    },
    isUnfocused() {
      return this.transaction.public_id && this.SELECTED_TRANSACTION.public_id && this.transaction.public_id !== this.SELECTED_TRANSACTION.public_id;
    },
    getMultiPayClass() {
      if (!this.transaction.multiBill) {
        return this.transaction.billPaymentEnabled ? '' : 'multi-off';
      }

      return this.transaction.billPaymentEnabled ? 'multi-on' : 'multi-off';
    },
    getBillTypeClass() {
      if (this.transaction.type === SUBSCRIPTION) {
        return 'subscription';
      }

      if (this.transaction.type === TARIFF) {
        return 'tariff';
      }

      return '';
    },
    formattedAmount() {
      const { amount, commission } = this.transaction;

      if (this.$validate.isNull(amount)) {
        return null;
      }

      return formatAmount(amount + commission, this.currency);
    },
    formattedBillAmount() {
      if (this.transaction.type === CASUAL) {
        const { amount, commission } = this.transaction.billTariffs[0];

        if (this.$validate.isNull(amount)) {
          return '';
        }

        return formatAmount(amount + commission, this.currency);
      }

      const amountList = this.getAmountList();

      if (this.$validate.isEmptyArray(amountList)) {
        return '';
      }

      if (this.$validate.isFixedLengthArray(amountList, 1)) {
        return formatAmount(amountList[0], this.currency);
      }

      return this.$t('amount.from', {
        displayAmount: formatAmount(Math.min(...amountList), this.currency),
      });
    },
    isProcessingSafeDeal() {
      const { deal } = this.transaction;

      if (this.$validate.isNull(deal)) {
        return false;
      }

      return deal.status === 'OPENED' || deal.status === 'CHALLENGE_PROCESSING';
    },
    isShownRating() {
      const { rating } = this.transaction;

      if (!this.$validate.isObjectWithFields(rating, ['rating'])) {
        return false;
      }

      return this.$validate.isPositiveNumber(rating.rating);
    },
    isCurrencyCrypto() {
      const { currency } = this.transaction;

      return currency.type === 'CRYPTO';
    },
    ...mapState([
      'TRANSACTIONS',
      'SELECTED_TRANSACTION',
    ]),
    ...mapGetters([
      'isDeviceSize',
    ]),
  },
  methods: {
    selectTransaction() {
      return this.SELECT_TRANSACTION(this.transaction);
    },
    getAmountList() {
      return (this.transaction.billTariffs || []).reduce((acc, curr) => {
        const { amount, commission } = curr;

        if (this.$validate.isNotNull(amount)) {
          acc.push(amount + commission);
        }

        return acc;
      }, []);
    },
    // eslint-disable-next-line complexity
    getTextByOrderType(orderType) {
      switch (orderType) {
        case 'PAYOUT':
          return this.$t('common.withdrawal');
        case 'PURCHASE':
          return this.$t('common.transfer');
        case 'PURCHASE_BILL':
          return this.$t('common.transfer');
        case 'PARTNER_PAYOUT_ACCRUAL':
          return this.$t('common.transfer');
        case 'BILL':
          return this.$t('common.bill');
        case 'MULTIBILL':
          return this.$t('common.multibill');
        case 'SUBSCRIPTION':
          return this.$t('bill.subscription');
        case 'TARIFF':
          return this.$t('bill.tariff');
        case 'TICKET':
          return this.$t('bill.ticket');
        case 'TIPS':
          return this.$t('bill.tips');
        case 'REFUND':
          return this.$t('common.refund');
        case 'CONFIRM_CARD':
          return this.$t('common.confirm.card');
        case 'REFUND_DEBIT':
          return this.$t('common.refund.debit');
        case 'SERVICE_DEBIT_MERCHANT':
          return this.$t('service.debit.merchant');
        default:
          return orderType;
      }
    },
    ...mapMutations(['SELECT_TRANSACTION']),
  },
};
</script>

<style lang="sass" module>
  .safeDealIcon
    display: inline-flex
    margin-right: 5px

    svg
      width: 16px
      height: 16px

      path
        fill: #B4B4B4
</style>
